import React, { useMemo, useEffect } from 'react';
import { observer } from 'src/utils/mobx-react';
import { DateRange, DayPicker } from 'react-day-picker';
import { Button } from 'src/domains/common/components/button/button';
import { DatePickerStore } from './date-picker.store';
import { DatePickerPropsType } from './date-picker.types';
import { ClickOutsideHandler } from 'src/domains/common/components/clickOutside';

export const DatePicker = observer(
    'DatePicker',
    ({ selectedDate, onDateSelect, buttonText = 'Select Date', className, disabled = false }: DatePickerPropsType) => {
        const store = useMemo(() => new DatePickerStore(), []);

        useEffect(() => {
            store.setSelectedDate(selectedDate);
        }, [selectedDate, store]);

        const handleDateSelect = (date: DateRange | undefined): void => {
            store.setSelectedDate(date);
            onDateSelect?.(date);
        };

        const handleMonthChange = (month: Date): void => {
            store.setDisplayedMonth(month);
        };

        const containerClassName =
            className !== undefined && className.trim() !== '' ? `relative ${className}` : 'relative';

        return (
            <div className={containerClassName}>
                <Button
                    onClick={store.toggle}
                    icon='calendar'
                    variant='primary'
                    disabled={disabled}
                >
                    {buttonText}
                </Button>

                {store.isOpen ? (
                    <ClickOutsideHandler onClickOutside={store.close}>
                        <div className='absolute top-full left-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg p-3 w-[248px] shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),_0px_1px_3px_0px_rgba(0,0,0,0.10)]'>
                            <DayPicker
                                mode='range'
                                selected={store.selectedDate}
                                onSelect={handleDateSelect}
                                month={store.displayedMonth}
                                onMonthChange={handleMonthChange}
                                showOutsideDays={true}
                                classNames={{
                                    caption: 'flex justify-center items-center mb-4 relative',
                                    caption_label: 'text-sm font-medium text-gray-900 mx-8',
                                    nav: 'flex items-center absolute inset-0',
                                    nav_button:
                                        'appearance-none border border-gray-200 bg-white p-0 m-0 inline-flex items-center justify-center w-7 h-7 text-gray-400 rounded shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500',
                                    nav_button_previous: 'absolute left-0',
                                    nav_button_next: 'absolute right-0',
                                    nav_icon: 'size-2 text-gray-400',
                                    table: 'w-full border-separate border-spacing-0 m-0 p-0',
                                    head_row: 'flex border-none',
                                    head_cell:
                                        'flex-1 text-center text-xs font-medium text-gray-500 pb-2 p-0 m-0 border-none',
                                    tbody: 'mt-2',
                                    row: 'flex',
                                    cell: 'flex-1 text-center p-0 m-0 border-none',
                                    button: 'appearance-none border-none p-0 m-0 w-7 h-7 text-sm text-gray-900 rounded flex items-center justify-center cursor-pointer',
                                    day: 'bg-transparent hover:bg-gray-100',
                                    day_today: 'bg-gray-100 !font-bold',
                                    day_selected: '!bg-blue-500 !text-white',
                                    day_outside: '!text-gray-400',
                                    day_disabled: '!text-gray-300 cursor-not-allowed',
                                }}
                            />
                        </div>
                    </ClickOutsideHandler>
                ) : null}
            </div>
        );
    }
);
